import { fetcher, useApplicantAPI, useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  PrimaryButton,
  showToast,
  Stack,
  Text,
} from "@certn/cascadia";
import {
  Loader,
  mq,
  SectionMessage,
  SummaryCard,
  WarningButton,
} from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import Markdown from "react-markdown";

import { useApplication } from "./ApplicationContext";
import { useApplicationFormContext } from "./ApplicationFormContext";

// Structural shape this component depends on. Both the applicant and CP QS
// OneID schemas (`OneIDVerifiedDataResponse` /
// `QuickscreenOneIDVerifiedDataResponse`) satisfy this — the backend produces
// them from the same action function. Defining the contract locally instead
// of casting between the two generated types means a future schema divergence
// fails at the type boundary instead of silently rendering broken UI.
type OneIdPollingShape = {
  should_poll_for_discrepancy_review?: boolean | null;
  discrepancy_review?: { is_ready?: boolean | null } | null;
};

const getPollingRefreshInterval = (data: OneIdPollingShape | undefined) => {
  if (
    !data ||
    (data.should_poll_for_discrepancy_review && !data.discrepancy_review) ||
    (data.discrepancy_review && !data.discrepancy_review.is_ready)
  ) {
    return 500;
  }
  return 0;
};

function Field({
  field,
  source,
}: {
  field: components["schemas"]["DiscrepancyReviewField"];
  source: "applicant" | "verified";
}) {
  const { completedSections, basePath = "apply" } = useApplication();
  const { caseId } = useApplicationFormContext();
  const { t } = useTranslation();
  const testID = `${source}-${field.name.replace("_", "-")}-label`;
  const value =
    field.name === "date_of_birth"
      ? formatDate(field[source], "PPP")
      : field[source];
  const showEditLink =
    source === "applicant" && completedSections?.includes(field.section);
  const editLink = showEditLink && (
    <Link
      textStyle="global/body-secondary"
      textColor="text-weak"
      href={`/${basePath}/${caseId}/${field.section}`}
      testID={`discrepancy-review-${field.section}-link`}
    >
      <Trans>Edit</Trans>
    </Link>
  );

  return (
    <div className={styles.item} data-test-id={testID}>
      <Text style="summary/content-secondary" color="text-weak">
        {field.label}
      </Text>
      <Stack orientation="horizontal" gap="space-1" justify="space-between">
        <Text
          style="summary/content-primary"
          color={value ? "text-base" : "text-disabled"}
        >
          {value ? String(value) : t("Not Applicable")}
        </Text>
        {editLink}
      </Stack>
    </div>
  );
}

export function IdentityDiscrepancyCheck({
  setCanSubmitCase,
  onDiscrepancyAccepted,
}: {
  setCanSubmitCase: (canSubmitCase: boolean) => void;
  onDiscrepancyAccepted: () => void;
}) {
  const { caseId } = useApplicationFormContext();
  const { basePath = "apply" } = useApplication();
  const isClientPortalQs = basePath === "quickscreen";

  // CP Quickscreen and Applicant OneID endpoints share the same response shape
  // (same backend action function) but live in independent generated schemas.
  // We narrow `data` back to the applicant type for the rest of the component
  // because every field accessed below already lives on
  // `OneIDVerifiedDataResponse`. A small runtime guard at the boundary
  // (`if (!data) return null` style) catches the case where a future schema
  // divergence on the CP side returns a fundamentally different shape.
  type VerifiedDataResponse =
    components["schemas"]["OneIDVerifiedDataResponse"];
  const { data: applicantData, isLoading: applicantLoading } = useApplicantAPI(
    isClientPortalQs
      ? null
      : `/api/internal/applicant/case/${caseId}/one-id/request-verified-data/`,
    {},
    { refreshInterval: getPollingRefreshInterval }
  );
  const { data: cpData, isLoading: cpLoading } = useClientPortalAPI(
    isClientPortalQs
      ? `/api/internal/client-portal/quickscreen-case/${caseId}/one-id/request-verified-data/`
      : null,
    {},
    { refreshInterval: getPollingRefreshInterval }
  );
  const data = (isClientPortalQs ? cpData : applicantData) as
    | VerifiedDataResponse
    | undefined;
  const isLoading = isClientPortalQs ? cpLoading : applicantLoading;

  const { mutate: applicantMutate } = useApplicantAPI(
    isClientPortalQs ? null : `/api/internal/applicant/case/${caseId}/`
  );
  const { mutate: cpMutate } = useClientPortalAPI(
    isClientPortalQs
      ? `/api/internal/client-portal/quickscreen-case/${caseId}/`
      : null
  );
  const mutate = isClientPortalQs ? cpMutate : applicantMutate;
  const { t } = useTranslation();
  const [
    showConfirmUseExistingDetailsModal,
    setShowConfirmUseExistingDetailsModal,
  ] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const [resolvedDiscrepancy, setResolvedDiscrepancy] = useState(false);

  useEffect(() => {
    if (resolvedDiscrepancy) {
      setCanSubmitCase(true);
      return;
    }
    if (isLoading || !data) {
      setCanSubmitCase(false);
      return;
    }
    if (
      data.discrepancy_review &&
      (data.discrepancy_review.has_discrepancy ||
        !data.discrepancy_review.is_ready)
    ) {
      setCanSubmitCase(false);
      return;
    }
    setCanSubmitCase(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    resolvedDiscrepancy,
    data?.discrepancy_review?.has_discrepancy,
    data?.discrepancy_review?.is_ready,
  ]);

  const showModalOrHandleUseExistingDetails = () => {
    if (
      data?.discrepancy_review?.show_confirmation_modal_on_applicant_details
    ) {
      setShowConfirmUseExistingDetailsModal(true);
    } else {
      handleUseExistingDetails();
    }
  };

  const handleUseExistingDetails = () => {
    setShowConfirmUseExistingDetailsModal(false);
    setResolvedDiscrepancy(true);
  };

  const handleUseVerifiedDetails = async () => {
    try {
      setDisableButtons(true);
      await fetcher.submit(
        isClientPortalQs
          ? `/api/internal/client-portal/quickscreen-case/${caseId}/one-id/accept-discrepancy/`
          : `/api/internal/applicant/case/${caseId}/one-id/accept-discrepancy/`,
        {}
      );
      await mutate();
      setDisableButtons(false);
      setResolvedDiscrepancy(true);
      onDiscrepancyAccepted();
    } catch (e) {
      showToast(t("Something went wrong updating your identity details."), {
        type: "error",
      });
      throw e;
    }
  };

  if (data && data.discrepancy_review && !data.discrepancy_review.is_ready) {
    return (
      <SummaryCard
        header={data.discrepancy_review.text.title}
        testID="discrepancy-section-card-loading"
      >
        <div className={styles.loaderLayout}>
          <Stack orientation="vertical" align="center" gap="space-10">
            <Loader type="spinner" />
            <Text style="summary/content-secondary">
              {t("Results pending, please wait...")}
            </Text>
          </Stack>
        </div>
      </SummaryCard>
    );
  }

  if (resolvedDiscrepancy) {
    return <></>;
  }

  return (
    <>
      {data?.discrepancy_review && data.discrepancy_review.has_discrepancy && (
        <>
          <SummaryCard
            header={data.discrepancy_review.text.title}
            testID="discrepancy-section-card"
          >
            <SectionMessage
              noBorderRadius
              icon="error"
              sectionTitle={data.discrepancy_review.text.error_heading}
              type="danger"
            >
              {data.discrepancy_review.text.error_description}
            </SectionMessage>
            <div className={styles.layout}>
              <div
                className={styles.container}
                data-testid="extracted-information"
              >
                <div className={styles.item}>
                  <Text style="global/heading-medium">
                    {data.discrepancy_review.text.verified_details_heading}
                  </Text>
                </div>

                {data.discrepancy_review.fields.map((field) => (
                  <Field key={field.name} field={field} source="verified" />
                ))}
                <div className={styles.item}>
                  <Stack gap="space-2">
                    <PrimaryButton
                      testID="use-extracted-id-button"
                      isDisabled={isLoading || disableButtons}
                      onPress={() => handleUseVerifiedDetails()}
                    >
                      {data.discrepancy_review.text.verified_details_button}
                    </PrimaryButton>
                    {data.discrepancy_review.text
                      .verified_details_button_disclaimer && (
                      <Text style="global/body-secondary">
                        {
                          data.discrepancy_review.text
                            .verified_details_button_disclaimer
                        }
                      </Text>
                    )}
                  </Stack>
                </div>
              </div>
              <div className={styles.divider} />
              <div
                className={styles.container}
                data-testid="applicant-provided-information"
              >
                <div className={styles.item}>
                  <Text style="global/heading-medium">
                    {data.discrepancy_review.text.applicant_details_heading}
                  </Text>
                </div>

                {data.discrepancy_review.fields.map((field) => (
                  <Field key={field.name} field={field} source="applicant" />
                ))}
                <div className={styles.item}>
                  <Stack gap="space-2">
                    <BasicButton
                      isDisabled={isLoading || disableButtons}
                      testID="details-match-button"
                      onPress={() => showModalOrHandleUseExistingDetails()}
                    >
                      {data.discrepancy_review.text.applicant_details_button}
                    </BasicButton>
                    {data.discrepancy_review.text
                      .applicant_details_button_disclaimer && (
                      <Text style="global/body-secondary">
                        {
                          data.discrepancy_review.text
                            .applicant_details_button_disclaimer
                        }
                      </Text>
                    )}
                  </Stack>
                </div>
              </div>
            </div>
          </SummaryCard>
          {data.discrepancy_review
            .show_confirmation_modal_on_applicant_details && (
            <Modal
              testID="use-applicant-details-confirmation-modal"
              isOpen={showConfirmUseExistingDetailsModal}
            >
              <Dialog>
                <DialogHeader
                  onClose={() => setShowConfirmUseExistingDetailsModal(false)}
                >
                  {
                    data.discrepancy_review.text
                      .applicant_details_confirmation_modal.title
                  }
                </DialogHeader>
                <DialogContent>
                  <Stack>
                    <Stack gap="space-6">
                      <Markdown
                        components={{
                          p: ({ children }) => (
                            <div>
                              <Text style="global/body-primary">
                                {children}
                              </Text>
                            </div>
                          ),
                        }}
                      >
                        {data.discrepancy_review.text
                          .applicant_details_confirmation_modal.body ?? ""}
                      </Markdown>
                    </Stack>
                    {data.discrepancy_review.text
                      .applicant_details_confirmation_modal.warning && (
                      <SectionMessage
                        type="warning"
                        icon="warning"
                        sectionTitle={
                          data.discrepancy_review.text
                            .applicant_details_confirmation_modal.warning
                        }
                      />
                    )}
                  </Stack>
                </DialogContent>
                <DialogFooter>
                  <BasicButton
                    onPress={() => setShowConfirmUseExistingDetailsModal(false)}
                  >
                    {
                      data.discrepancy_review.text
                        .applicant_details_confirmation_modal.cancel_button
                    }
                  </BasicButton>
                  {/* TODO: This is deprecated but @certn/cascadia does not export it */}
                  <WarningButton onPress={() => handleUseExistingDetails()}>
                    {
                      data.discrepancy_review.text
                        .applicant_details_confirmation_modal.confirm_button
                    }
                  </WarningButton>
                </DialogFooter>
              </Dialog>
            </Modal>
          )}
        </>
      )}
    </>
  );
}

const styles = {
  layout: css(
    {
      display: "flex",
      alignItems: "stretch",
      flex: 1,
    },
    mq({ flexDirection: ["column", "row"] })
  ),
  container: css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    "& > *:first-child": {
      marginTop: "var(--space-4)",
    },
  }),
  item: css({
    marginLeft: "var(--space-4)",
    marginRight: "var(--space-4)",
    marginBottom: "var(--space-4)",
  }),
  divider: css(
    {
      display: "flex",
      border: "none",
      margin: "none",
    },
    mq({
      borderLeft: ["none", "1px solid var(--border-base)"],
      borderTop: ["1px solid var(--border-base)", "none"],
      margin: ["0 var(--space-4)", "var(--space-4) 0"],
    })
  ),
  loaderLayout: css({
    paddingTop: "var(--space-4)",
    paddingBottom: "var(--space-4)",
  }),
};
